import { Paper, Popover, PopoverProps as MuiPopoverProps } from "@mui/material"
import { Check } from "@mui/icons-material"
import { AnimatePresence, motion } from "framer-motion"
import React from "react"
import styled from "styled-components"
import { theme } from "../../../theme"
import { ButtonArea } from "../../atoms/button"
import { Chip, ChipAction, ChipSize } from "../../atoms/chip"
import { ChipDropdownOption } from "./ChipDropdownOption"

type PropsFromMui = "transformOrigin" | "anchorOrigin"

export type ChipDropdownProps = {
  value: ChipDropdownOption | undefined
  options: ChipDropdownOption[]
  onChange?: (value: ChipDropdownOption) => void
  size?: ChipSize
  disabled?: boolean
  popoverConfig?: Pick<MuiPopoverProps, PropsFromMui>
  dataCy?: string
  defaultText?: string
}

export const ChipDropdown = (props: ChipDropdownProps) => {
  const {
    value,
    options,
    size,
    onChange,
    disabled,
    dataCy,
    defaultText,
    popoverConfig = {
      transformOrigin: {
        vertical: "top",
        horizontal: "left",
      },
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left",
      },
    },
  } = props

  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null)
  const open = Boolean(anchorEl)
  const defaultOption = options.find((option) => option.isDefault)
  const isActive = open || (!!defaultOption && value?.key !== defaultOption?.key)

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleChange = (option: ChipDropdownOption) => {
    if (onChange) {
      onChange(option)
      handleClose()
    }
  }

  return (
    <Root>
      <Chip
        disabled={disabled}
        key={value?.key}
        active={isActive}
        size={size}
        label={value?.label}
        icon={value?.icon}
        onClick={handleClick}
        action={ChipAction.Dropdown}
        dataCy={dataCy}
      />
      <Popover open={open} anchorEl={anchorEl} onClose={handleClose} {...popoverConfig}>
        <AnimatePresence>
          <Menu
            elevation={3}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ animate: { duration: 0.5 }, exit: { duration: 0.2 } }}
          >
            {options.map((option) => (
              <Item key={option.key} onClick={() => handleChange(option)} data-cy={option.dataCy}>
                {option.icon && <ItemIcon>{option.icon}</ItemIcon>}
                <ItemLabel>{option.label}</ItemLabel>
                {!!defaultText && option.isDefault && <DefaultText>{defaultText}</DefaultText>}
                {value?.key === option.key && <SelectedIcon />}
              </Item>
            ))}
          </Menu>
        </AnimatePresence>
      </Popover>
    </Root>
  )
}

const Root = styled.div``

const Item = styled(ButtonArea)`
  padding: 12px 16px;
  line-height: ${theme.lineHeight.m};
  cursor: pointer;
`

const Menu = styled(motion(Paper))`
  display: flex;
  flex-direction: column;
  min-width: 260px;
  color: ${theme.color.black1};

  .MuiButton-label {
    display: grid;
    grid-auto-flow: column;
    grid-gap: 6px;
    align-items: center;
    grid-template-columns: max-content;
  }
`

const ItemIcon = styled.span`
  display: flex;
  margin-right: ${theme.spacing.s};
`

const ItemLabel = styled.span``

const SelectedIcon = styled(Check)`
  margin-left: auto;
`

const DefaultText = styled.span`
  margin-left: ${theme.spacing.s};
  color: ${theme.color.grey2};
`
